import React from "react";
import { InlineMath } from "react-katex";
import TooltipLink from "../components/TooltipLink";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";

/**
 * 텍스트 내에서 인라인 수식과 마크다운을 처리하는 함수
 * @param text 처리할 텍스트
 * @param options 옵션 (enableDebug: 디버깅 로그 활성화, footnotes: 각주 정의 맵)
 * @returns React 노드 배열
 */
export function processTextContent(
  text: string,
  options?: { enableDebug?: boolean; footnotes?: Map<string, string> }
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];

  // 디버깅: 첫 렌더링 시 문제 확인
  if (options?.enableDebug && process.env.NODE_ENV === "development" && text.includes("${")) {
    console.log("processTextContent: 템플릿 리터럴 감지", text.substring(0, 100));
  }

  // 마크다운 이미지 (![alt](url)), LaTeX 수식 ($...$), 인라인 코드 (`...`), 마크다운 굵은 글씨 (**...**), 밑줄 (<u>...</u>), 각주 참조 ([^n]), 마크다운 링크 ([text](<url>) 우선, [text](url)) 패턴을 모두 찾기
  // 이미지 패턴은 링크 패턴보다 앞에 배치하여 ![alt](url)이 링크로 오인되지 않도록 함
  // ${...} 패턴은 제외 (템플릿 리터럴이 평가되지 않은 경우)
  // 각주 참조 (\[\^\d+\])는 링크 패턴보다 앞에 배치하여 [^1] 형태가 링크로 오인되지 않도록 함
  const combinedRegex =
    /(!\[[^\]]*\]\([^)]+\)|\$(?!\{)[^$]+\$|`[^`]+`|\*\*[^*]+\*\*|<u>[^<]+<\/u>|\[\^\d+\]|\[[^\]]+\]\(<[^>]+>\)|\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = combinedRegex.exec(text)) !== null) {
    // 매치 앞의 일반 텍스트 추가
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const matchedText = match[1];

    if (matchedText.startsWith("![")) {
      // 마크다운 이미지 처리 ![alt](url) 또는 ![image|width](url)
      const imgMatch = matchedText.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (imgMatch) {
        const [, rawAlt, src] = imgMatch;
        const widthMatch = rawAlt.match(/\|(\d+)$/);
        const imgWidth = widthMatch ? parseInt(widthMatch[1]) : undefined;
        const alt = rawAlt.replace(/\|\d+$/, "") || "첨부 이미지";
        parts.push(
          <a
            key={`img-${keyIndex++}`}
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="block my-1"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={src}
              alt={alt}
              style={imgWidth ? { width: `${imgWidth}px`, maxWidth: "100%" } : undefined}
              className={`${imgWidth ? "" : "max-w-[300px] "}max-h-[300px] object-contain rounded-md border border-gray-200 dark:border-neutral-500 hover:opacity-80 transition-opacity`}
              loading="lazy"
            />
          </a>
        );
      }
    } else if (matchedText.startsWith("$") && matchedText.endsWith("$")) {
      // LaTeX 인라인 수식 처리
      const mathContent = matchedText.slice(1, -1); // $ 제거
      parts.push(
        <InlineMath key={`math-${keyIndex++}`} math={mathContent} />
      );
    } else if (matchedText.startsWith("`") && matchedText.endsWith("`")) {
      // 인라인 코드 처리
      const codeContent = matchedText.slice(1, -1); // ` 제거
      parts.push(
        <code
          key={`code-${keyIndex++}`}
          className="whitespace-normal break-words rounded border border-border bg-muted px-1 py-0 font-mono text-[0.9em] leading-relaxed text-foreground"
        >
          {codeContent}
        </code>
      );
    } else if (matchedText.startsWith("**") && matchedText.endsWith("**")) {
      // 마크다운 굵은 글씨 처리
      const boldContent = matchedText.slice(2, -2); // ** 제거
      parts.push(
        <strong key={`bold-${keyIndex++}`} className="font-bold">
          {boldContent}
        </strong>
      );
    } else if (matchedText.startsWith("<u>") && matchedText.endsWith("</u>")) {
      // 밑줄 처리 (예: DB 기본키 속성 표시)
      const underlineContent = matchedText.slice(3, -4); // <u> </u> 제거
      parts.push(
        <span key={`u-${keyIndex++}`} className="underline">
          {underlineContent}
        </span>
      );
    } else if (matchedText.startsWith("[^") && matchedText.endsWith("]")) {
      // 각주 참조 처리 ([^1], [^2], ...)
      const noteId = matchedText.slice(2, -1);
      const definition = options?.footnotes?.get(noteId);
      if (definition) {
        parts.push(
          <Tooltip tapToOpen key={`fn-ref-${keyIndex++}`}>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`각주 ${noteId} 보기`}
                className="ml-0.5 inline cursor-help align-super text-[0.75em] font-semibold leading-none text-blue-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-blue-400"
              >
                [{noteId}]
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="max-w-80 text-sm leading-relaxed md:!max-w-2xl !w-auto !text-left !text-wrap"
            >
              <div className="text-left">
                {processTextContent(definition)}
              </div>
            </TooltipContent>
          </Tooltip>
        );
      } else {
        parts.push(
          <sup key={`fn-ref-${keyIndex++}`} className="text-gray-500 ml-0.5">
            [{noteId}]
          </sup>
        );
      }
    } else if (matchedText.startsWith("[") && matchedText.includes("](")) {
      // 마크다운 링크 처리 [text](<url>) 또는 [text](url)
      let linkText: string;
      let href: string;

      if (matchedText.includes("](<") && matchedText.endsWith(">)")) {
        // 꺽쇠 브라켓으로 감싸진 URL [text](<url>)
        const linkMatch = matchedText.match(/^\[([^\]]+)\]\(<([^>]+)>\)$/);
        if (linkMatch) {
          [, linkText, href] = linkMatch;
        } else {
          continue;
        }
      } else {
        // 일반 URL [text](url)
        const linkMatch = matchedText.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
          [, linkText, href] = linkMatch;
        } else {
          continue;
        }
      }

      // 내부 링크인지 확인 (/ 또는 # 으로 시작)
      if (href.startsWith("/") || href.startsWith("#")) {
        parts.push(
          <TooltipLink
            key={`link-${keyIndex++}`}
            href={href}
            text={linkText}
            IconClassName="size-3"
            TextClassName="text-base"
            LinkClassName="inline-flex"
            ButtonClassName="!p-0 !h-auto !gap-1 !items-baseline !mr-1"
          />
        );
      } else {
        // 외부 링크
        parts.push(
          <a
            key={`link-${keyIndex++}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {linkText}
          </a>
        );
      }
    }

    lastIndex = match.index + match[0].length;
  }

  // 남은 텍스트 추가
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}
